/**
 * Public, admin-editable site content (GET /api/content, stored in ADMIN_KV by /admin):
 *  - announcement banner (dismissible per text)
 *  - THALIA line overrides (applied to holo/thaliaLines)
 *  - Faction Wars schedule (next / live event chip)
 * Renders nothing when the API is not configured or unreachable.
 */
import { useEffect, useState } from 'react';
import { setLineOverrides, type LineCat } from '../holo/thaliaLines';

interface Content {
  banner: { enabled: boolean; text: string; level: 'info' | 'warn' | 'event'; link?: string; updatedAt?: number } | null;
  thalia: { mode: 'append' | 'replace'; lines: Partial<Record<LineCat, string[]>> } | null;
  schedule: { id: string; title: string; start: string; end?: string; factions?: string; note?: string }[];
}
let cache: Promise<Content | null> | null = null;
export function loadSiteContent(force = false): Promise<Content | null> {
  if (!cache || force) cache = fetch('/api/content', { headers: { accept: 'application/json' } })
    .then(r => (r.ok ? r.json() : null)).catch(() => null)
    .then((c: Content | null) => { if (c?.thalia?.lines) setLineOverrides(c.thalia.lines, c.thalia.mode); return c; });
  return cache;
}
function useContent() {
  const [c, setC] = useState<Content | null>(null);
  useEffect(() => { let on = true; loadSiteContent().then(v => { if (on) setC(v); }); return () => { on = false; }; }, []);
  return c;
}

const DISMISS = 'nt3d_banner_dismissed';
const COLORS = { info: 'var(--c)', warn: 'var(--y)', event: 'var(--o)' } as const;

export function SiteAnnouncement() {
  const c = useContent();
  const b = c?.banner;
  const sig = b ? `${b.updatedAt || 0}:${b.text}` : '';
  const [hidden, setHidden] = useState(false);
  useEffect(() => { try { setHidden(localStorage.getItem(DISMISS) === sig); } catch { /* ignore */ } }, [sig]);
  if (!b || !b.enabled || !b.text || hidden) return null;
  const col = COLORS[b.level] || COLORS.info;
  return (
    <div className="site-announce" role="status" style={{ borderColor: col }} data-testid="site-announcement">
      <span className="sa-dot" style={{ background: col }} aria-hidden="true" />
      <span className="sa-text">{b.text}</span>
      {b.link && <a href={b.link} className="sa-link" style={{ color: col }} {...(b.link.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}>More</a>}
      <button type="button" className="sa-x" aria-label="Dismiss announcement" onClick={() => { try { localStorage.setItem(DISMISS, sig); } catch { /* ignore */ } setHidden(true); }}>×</button>
    </div>
  );
}

/** "FACTION WARS · live / in 3h 20m" chip for the event slot. */
export function FactionWarsChip() {
  const c = useContent();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);
  const items = (c?.schedule || []).map(s => ({ ...s, s: Date.parse(s.start), e: s.end ? Date.parse(s.end) : Date.parse(s.start) + 3600_000 }))
    .filter(s => s.e > now).sort((a, b) => a.s - b.s);
  const ev = items[0];
  if (!ev) return null;
  const live = ev.s <= now;
  const mins = Math.round((ev.s - now) / 60000);
  const when = live ? 'LIVE NOW' : mins < 60 ? `in ${mins}m` : mins < 2880 ? `in ${Math.floor(mins / 60)}h ${mins % 60}m` : new Date(ev.s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return (
    <span className="faction-wars-chip" title={[ev.title, ev.factions, ev.note, new Date(ev.s).toLocaleString()].filter(Boolean).join(' · ')} data-testid="faction-wars-chip">
      ⚔ {ev.title} <b>{when}</b>
    </span>
  );
}
